/**
 * CharacterAvatar — compatibility shim around LimezuAvatar.
 *
 * Phase 11 used a procedural Phaser-graphics character (skin / hairStyle /
 * shirtColor blobs). Phase 12.1 retires that in favour of layered LimeZu
 * spritesheets (see ./avatar/LimezuAvatar.ts).
 *
 * To keep MainScene's existing call sites working without a sprawling diff,
 * this module re-exports a `CharacterAvatar` symbol that wraps the new
 * LimezuAvatar with the old API surface:
 *
 *   - constructor(scene, x, y)
 *   - setAppearance(...) — accepts both old (skin/hairStyle/etc.) and new
 *     (bodyIdx/hairFamily/etc.) shapes; old values map to defaults.
 *   - setFacingDirection(d)  → LimezuAvatar.setDirection
 *   - setMoving(m)           → LimezuAvatar.setMoving
 *   - setPose(p)             → no-op for now (animation rows TBD)
 *   - setEffect(e)           → no-op (FX layer system to be revived later)
 *   - syncTo(x, y)           → LimezuAvatar.setPosition
 *   - update(dt)             → LimezuAvatar.update
 *   - destroy()
 *
 * The exported palettes (HAIR_STYLES, SKIN_TONES, etc.) remain so that any
 * legacy import compiles. They're vestigial in the LimeZu world.
 */
import Phaser from "phaser";
import {
  Appearance as LimezuAppearance,
  DEFAULT_APPEARANCE as LIMEZU_DEFAULTS,
  LimezuAvatar,
} from "./avatar/LimezuAvatar";
import { Direction } from "./avatar/atlas";

/** Compat appearance — accepts both old procedural fields and new LimeZu indices. */
export interface Appearance extends Partial<LimezuAppearance> {
  // Legacy procedural fields — accepted but not used. Kept to keep TS happy
  // for older code paths that pass these.
  skin?: string;
  hairStyle?: string;
  hairColor?: string;
  eyeColor?: string;
  shirtStyle?: string;
  shirtColor?: string;
  pantsColor?: string;
}

export const DEFAULT_APPEARANCE: LimezuAppearance = LIMEZU_DEFAULTS;

// Vestigial palettes — kept so that any legacy import compiles. They map
// roughly onto the new LimeZu families but are not used at runtime.
export const HAIR_STYLES = ["short", "long", "spiky", "ponytail", "buzz", "afro", "wavy", "bald"] as const;
export const SHIRT_STYLES = ["tee", "hoodie", "tank", "blazer", "tuxedo"] as const;
export const SKIN_TONES = ["#fde6c4", "#f6c5a4", "#e6a47e"] as const;
export const HAIR_COLORS = ["#1f1330", "#3b1f1f", "#7c2d12"] as const;
export const EYE_COLORS = ["#1f2937", "#15803d", "#1d4ed8"] as const;
export const SHIRT_COLORS = ["#3b82f6", "#dc2626", "#10b981"] as const;
export const PANTS_COLORS = ["#1e3a8a", "#0f172a"] as const;

export type Pose = "idle" | "sit" | "wave" | "dance";
export type Effect = "none" | "wings" | "aura" | "trail";

function toLimezuAppearance(a: Appearance | LimezuAppearance | undefined): LimezuAppearance {
  if (!a) return { ...LIMEZU_DEFAULTS };
  // If new-style, just merge.
  if ("bodyIdx" in a || "hairFamily" in a || "outfitFamily" in a) {
    return { ...LIMEZU_DEFAULTS, ...(a as Partial<LimezuAppearance>) };
  }
  // Old-style — fall back to defaults (procedural color → no clean mapping
  // to LimeZu sprite family).
  return { ...LIMEZU_DEFAULTS };
}

export class CharacterAvatar {
  private inner: LimezuAvatar;
  private direction: Direction = "down";
  private moving = false;

  constructor(scene: Phaser.Scene, x: number, y: number, initial?: Appearance) {
    const appearance = toLimezuAppearance(initial);
    this.inner = new LimezuAvatar(scene, x, y, appearance);
    void this.inner.start();
  }

  setAppearance(a: Partial<Appearance | LimezuAppearance>): void {
    if ("bodyIdx" in a || "hairFamily" in a || "outfitFamily" in a) {
      void this.inner.setAppearance(a as Partial<LimezuAppearance>);
      return;
    }
    // Old-style update — ignored. The CharacterCreator now writes new-style
    // fields; old fields are effectively a no-op.
  }

  setFacingDirection(dir: Direction | string): void {
    this.setDirection(dir);
  }

  setDirection(dir: Direction | string): void {
    if (dir === "up" || dir === "down" || dir === "left" || dir === "right") {
      this.direction = dir;
      this.inner.setDirection(dir);
    }
  }

  setPosition(x: number, y: number): void {
    this.inner.setPosition(x, y);
  }

  setMoving(moving: boolean): void {
    this.moving = moving;
    this.inner.setMoving(moving);
  }

  private currentPose: Pose = "idle";

  /** Pose changes are not yet wired in the LimeZu avatar (animation rows TBD). */
  setPose(pose: Pose): void {
    this.currentPose = pose;
    // no-op visual until anim rows decoded
  }

  getPose(): Pose {
    return this.currentPose;
  }

  /** FX layer not yet ported to LimeZu — restore in a polish pass. */
  setEffect(_effect: Effect): void {
    // no-op for now
  }

  syncTo(x: number, y: number): void {
    this.inner.setPosition(x, y);
  }

  setDepth(depth: number): void {
    this.inner.setDepth(depth);
  }

  update(delta: number): void {
    this.inner.update(delta);
  }

  destroy(): void {
    this.inner.destroy();
  }

  getContainer(): Phaser.GameObjects.Container {
    return this.inner.getContainer();
  }
}
